<script>
import EgDropdown from "@/components/global/eg_dropdown.vue";
import UppyDashboard from "@/components/dashboard/uppy_dashboard.vue";
import { getFreeToolsRubrics } from "@/api/requests/freeTools";

import { essayExample } from "./essayExample";
import EgButton from "@/components/global/buttons/eg_button.vue";
import EgCustomRadio from "@/components/global/buttons/eg_custom_radio.vue";
import EgCustomRadioGroup from "@/components/global/buttons/eg_custom_radio_group.vue";

const gradeIntensities = [
  { text: "Easy", value: "light" },
  { text: "Normal", value: "moderate" },
  { text: "Strict", value: "intense" },
];

const essayOptions = [
  { text: "Upload a file", value: "files" },
  { text: "Enter text", value: "text" },
];

export const gradeLevels = [
  {
    label: "Elementary",
    icon: "triangle-fill",
    color: "#28A745",
    backgroundColor: "#28A7451A",
    value: "elementary",
  },
  {
    label: "Middle School",
    icon: "square-fill",
    color: "#E27247",
    backgroundColor: "#E272471A",
    value: "middleschool",
  },
  {
    label: "High School",
    icon: "pentagon-fill",
    color: "#2196F3",
    backgroundColor: "#2196F31A",
    value: "highschool",
  },
  {
    label: "College",
    icon: "hexagon-fill",
    color: "#CE00DD",
    backgroundColor: "#CE00DD1A",
    value: "college",
  },
];

export default {
  name: "UploadEssayStep",
  components: {
    UppyDashboard,
    EgButton,
    EgDropdown,
    EgCustomRadio,
    EgCustomRadioGroup,
  },
  props: {
    selectedEssayOption: {
      type: String,
      default: "",
    },
    essayContents: {
      type: String,
      default: "",
    },
    selectedGradingLevel: {
      type: String,
      default: "",
    },
    selectedGradingIntensity: {
      type: String,
      default: "",
    },
    files: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    "selectEssayOption",
    "setEssayContent",
    "selectGradingLevel",
    "selectRubric",
    "selectGradingIntensity",
    "grade",
    "file-added",
    "file-removed",
    "file-uploaded",
  ],
  data() {
    return {
      gradeIntensities,
      essayOptions,
      gradeLevels,
      essayExample,
      registerLink: "https://app.essaygrader.ai/register",
      rubrics: [],
      selectedRubric: null,
      isFocused: false,
      userModifiedGradingLevel: false,
      userModifiedGradingIntensity: false,
    };
  },
  watch: {
    selectedRubric(v) {
      this.$emit("selectRubric", v);
      if (v?.gradeLevel && !this.userModifiedGradingLevel) {
        this.$emit("selectGradingLevel", v.gradeLevel);
      }
      if (v?.intensity && !this.userModifiedGradingIntensity) {
        this.$emit("selectGradingIntensity", v.intensity);
      }
    },
  },
  async mounted() {
    await this.fetchRubrics();
  },

  methods: {
    async fetchRubrics() {
      const rubricsQuery = {};

      if (this.$route.query.rubricName) {
        rubricsQuery.name = this.$route.query.rubricName;
      }
      if (this.$route.query.gradeLevel) {
        rubricsQuery.gradeLevel = this.$route.query.gradeLevel;
      }

      const { data } = await getFreeToolsRubrics(rubricsQuery);

      this.rubrics = data;
      this.selectedRubric = data[0];
    },

    upload() {
      return this.$refs.uppyDashboard.upload();
    },
    handleLinkClick() {
      this.$emit("setEssayContent", essayExample);
      this.$emit("selectEssayOption", "text");
    },
    focusTextarea() {
      this.$el.querySelector("textarea").focus();
    },
    handleGradingLevelClick(value) {
      this.userModifiedGradingLevel = true;
      this.$emit("selectGradingLevel", value);
    },
    handleGradingIntensityClick(value) {
      this.userModifiedGradingIntensity = true;
      this.$emit("selectGradingIntensity", value);
    },
  },
};
</script>

<template>
  <div class="upload-essay-step">
    <!-- Header with all grading options -->
    <div class="options-header">
      <!-- Student Level -->
      <div class="header-section">
        <label class="header-label">LEVEL</label>
        <eg-custom-radio-group
          :options="gradeLevels"
          :value="selectedGradingLevel"
          @input="handleGradingLevelClick"
        />
      </div>

      <!-- Rubric -->
      <div class="header-section header-section--rubric">
        <label class="header-label">RUBRIC</label>
        <eg-dropdown class="rubrics-dropdown">
          <template #button-content>
            <div v-if="selectedRubric" class="rubrics-dropdown-content">
              <div class="rubrics-dropdown-text" :title="selectedRubric.name">
                <span>{{ selectedRubric.name }}</span>
              </div>
              <b-icon class="ml-auto" icon="chevron-down"></b-icon>
            </div>
          </template>
          <b-dropdown-item-button
            v-for="rubric in rubrics"
            :key="rubric.id"
            class="rubric-dropdown-item"
            @click="() => (selectedRubric = rubric)"
          >
            {{ rubric.name }}
          </b-dropdown-item-button>
          <b-dropdown-divider />
          <eg-button
            :href="registerLink"
            class="custom-rubric outline mx-4 my-1"
            target="_blank"
          >
            Create custom rubric
          </eg-button>
        </eg-dropdown>
      </div>

      <!-- Grading Intensity -->
      <div class="header-section">
        <label class="header-label">INTENSITY</label>
        <eg-custom-radio-group
          :options="gradeIntensities"
          :value="selectedGradingIntensity"
          @input="handleGradingIntensityClick"
        />
      </div>
    </div>

    <!-- Essay input tabs -->
    <div class="essay-input-tabs">
      <eg-custom-radio
        v-for="option in [
          { label: 'Enter Manually', value: 'text', icon: 'file-document' },
          { label: 'Upload File', value: 'files', icon: 'upload' },
        ]"
        :key="option.value"
        :is-selected="selectedEssayOption === option.value"
        :icon="option.icon"
        @click="$emit('selectEssayOption', option.value)"
      >
        {{ option.label }}
      </eg-custom-radio>
    </div>

    <!-- Content area -->
    <div class="content-area">
      <div
        v-if="!essayContents && !isFocused && selectedEssayOption === 'text'"
        class="placeholder-overlay"
        @mousedown.prevent="focusTextarea"
      >
        Paste your student's essay here or
        <b-link @click="handleLinkClick" @mousedown.stop>
          use the sample essay
        </b-link>
        to get started...
      </div>

      <uppy-dashboard
        v-if="selectedEssayOption === 'files'"
        ref="uppyDashboard"
        class="uppy"
        :path="`free-tools`"
        :value="files"
        plugin-name-camera-title="Handwriting"
        :accept-services="['oneDrive', 'dropbox']"
        :max-number-of-files="1"
        @input="$emit('file-uploaded', $event)"
        @file-added="$emit('file-added', $event)"
        @file-removed="$emit('file-removed', $event)"
      />
      <b-form-textarea
        v-if="selectedEssayOption === 'text'"
        class="textarea"
        :value="essayContents"
        @focus="isFocused = true"
        @blur="isFocused = false"
        @update="$emit('setEssayContent', $event)"
      />
    </div>

    <!-- Grade Essay Button -->
    <div class="grade-button-container">
      <eg-button
        class="grade-button"
        icon="stars"
        type="primary"
        :disabled="
          (selectedEssayOption === 'files' && files.length === 0) ||
          (selectedEssayOption === 'text' && !essayContents)
        "
        @click="$emit('grade')"
      >
        Grade Essay
      </eg-button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.upload-essay-step {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: white;
  max-width: 1134px;
  gap: 16px;
  border-bottom-right-radius: 16px;
  border-bottom-left-radius: 16px;
  * {
    font-family: system-ui, -apple-system, sans-serif;
  }
}

// Header with all grading options
.options-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  /*background: linear-gradient(135deg, #47308E 0%, #6A50F3 50%, #AC73FF 100%);*/
  background: #6a50f3;
  flex-wrap: wrap;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  @media (max-width: 1110px) {
    flex-direction: row;
    flex-wrap: wrap;
  }
}

.header-section {
  display: flex;
  align-items: center;
  gap: 12px;

  @media (min-width: 900px) {
    flex-direction: column;
    &.header-section--rubric {
      flex: 1;
    }
  }
}

.header-label {
  font-family: var(--typography-font-family-text-primary);
  font-size: 11px;
  line-height: 1;
  letter-spacing: 0.5px;
  color: white;
  white-space: nowrap;
  margin: 0;
}
// Rubric dropdown in header
.rubrics-dropdown {
  flex: 1;
  @media (min-width: 820px) {
    width: 100%;
  }
  ::v-deep .btn {
    height: 38px;
    width: 276px;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    background: white;
    font-size: 14px;
    border-radius: 30px;
    padding: 0;
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.2);
    @media (max-width: 1110px) {
      white-space: normal;
    }
    @media (min-width: 820px) {
      width: 100%;
    }
  }

  ::v-deep .dropdown-item {
    white-space: normal;
    height: auto;
    padding: 8px 16px;
    &.active,
    &:active {
      background-color: #6a50f3;
    }
  }
  ::v-deep .dropdown-menu {
    width: 450px !important;
  }
  ::v-deep .bi-chevron-down {
    color: white;
    font-size: 88%;
  }
}

.rubrics-dropdown-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
  border-radius: 30px;
  padding: 0 16px;
}

.rubrics-dropdown-text {
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
    height: 16px;
    line-height: initial;
    color: white;
    width: 220px;
    white-space: nowrap;
    @media (min-width: 920px) {
      width: 100%;
      max-width: 300px;
    }
  }
}

// Essay input tabs
.essay-input-tabs {
  display: flex;
  gap: 8px;
  padding: 0 16px;

  ::v-deep .eg-new-button {
    border-radius: 12px;
    padding: 10px 20px;
    font-weight: 500;
    font-size: 14px;

    // Inactive state
    &.eg-new-button--secondary {
      background-color: white;
      border-color: #e6e6e6;
      color: #6b7280;

      &:hover {
        background-color: #f9fafb;
        border-color: #d1d5db;
      }
    }

    // Active state
    &.eg-new-button--primary {
      background-color: #6a50f3;
      border-color: #6a50f3;
      color: white;

      &:hover {
        background-color: #5940d9;
      }
    }
  }
}

// Content area
.content-area {
  flex: 1;
  padding: 0 16px;
  position: relative;
  overflow: auto;
}

.placeholder-overlay {
  position: absolute;
  top: 25px;
  left: 48px;
  color: #9ca3af;
  pointer-events: none;
  font-size: 14px;

  ::v-deep a {
    pointer-events: all;
    color: #6a50f3;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: #5940d9;
      text-decoration: none;
    }
  }
}

.textarea {
  width: 100%;
  min-height: 280px;
  height: 100%;
  border-radius: 12px;
  resize: none;
  border: 2px dashed #e6e6e6;
  padding: 16px;
  font-family: var(--typography-font-family-text-primary);
  font-size: 14px;
  line-height: 1.6;
  background: #fafafa;
  &:focus {
    outline: none;
    border-color: #6a50f3;
    box-shadow: none;
  }
}

.uppy ::v-deep {
  .uppy-Dashboard-inner,
  .uppy-Dashboard-innerWrap,
  .uppy-Dashboard-AddFiles {
    border-radius: 12px;
  }

  .uppy-Dashboard {
    height: 400px;
  }

  .uppy-Dashboard-Item-preview {
    height: 130px;
  }

  .uppy-Dashboard-poweredBy {
    display: none !important;
  }

  .uppy-Dashboard-inner {
    background: #ffffff;
  }

  .uppy-Dashboard-AddFiles {
    border: 2px dashed #e6e6e6;
    background: #fafafa;
  }

  .uppy-Dashboard-AddFiles-list {
    [data-uppy-acquirer-id="MyDevice"] {
      display: none;
    }
  }
}

// Grade button container
.grade-button-container {
  padding: 0 16px 16px 16px;
  display: flex;
  justify-content: end;
}

.grade-button {
  min-width: 200px;
  height: 48px;
  font-size: 16px;

  ::v-deep {
    padding: 12px 32px;
  }
}

.btn.custom-rubric {
  height: 36px;
}
</style>
